import type { NextRouter } from 'next/router';
import { withRouter } from 'next/router';
import React, { useState } from 'react';
import styled from 'styled-components';
import { ieHideScroll } from '@core/styles';
import { media } from '@core/styles/breakpoints';
import { isDefined } from '@peloton/types/isDefined';
import { gray1 } from '@engage/colors/base';
import { spaces } from '@engage/styles/spaces';
import { SlideoverStackTransition } from '@members/animations/SlideoverStackTransition/SlideoverStackTransition';
import { TagsCopy } from '@members/copy/copy.generated';
import { Tags } from '@members/copy/models.generated/Tags';
import { TagBrowseCategorySlug } from '@members/graphql-swr/types.generated';
import {
  StandardModalContainer,
  modalHeight,
  tagsModalMaxHeight,
  tagsModalMinHeight,
} from '@members/modal/containers';
import CustomizableToast, { StyledMessageAndIcon } from '@members/notifications/Toast';
import { toTagsModalQuery } from '@members/routes/tags';
import { toAnalyticsTagView } from '../analytics/events';
import { MainView } from '../MainView/MainView';
import { PERSISTENT_NAV_BAR_VIEWS } from '../MainView/PersistentNavBar';
import { ReplaceTagView } from '../ReplaceTag/ReplaceTagView';
import { getTagViewFromLocation } from '../selectors';
import { TagModalView } from '../shared';
import { ConfirmHideTagView } from '../TagDetails/ConfirmHideTagView';
import { TagDetails } from '../TagDetails/TagDetails';
import { TagsContext } from '../TagsContext';
import { OtherMembersTagsView } from '../UserTags/UserTagsView';

interface WithRouterProps {
  router: NextRouter;
}

interface TagsModalViewProps extends WithRouterProps {}

type Props = TagsModalViewProps & {
  showErrorToast: boolean;
  setShowErrorToast: (b: boolean) => void;
};

type SourceProps = {
  tagView: TagModalView;
  category?: TagBrowseCategorySlug;
};

type QueryProps = {
  view: TagModalView;
  tagName?: string;
  browseCategory?: TagBrowseCategorySlug;
};

type ViewSetters = {
  setLastActiveBrowseCategory: (category: TagBrowseCategorySlug) => void;
  setLastActiveTagName: (tagName: string) => void;
  setViewStack: (stack: any) => void;
  setIsAutoHeight: (isAutoHeight: boolean) => void;
};

export const ConnectedTagsModalView: React.FC<React.PropsWithChildren<Props>> =
  React.memo(({ router: routerHistory, showErrorToast, setShowErrorToast }) => {
    const { CONTEXT_PROPS, viewStack } = useTagsModalProps(
      routerHistory,
      setShowErrorToast,
    );

    return (
      <TagsContext.Provider value={CONTEXT_PROPS}>
        <Container isAutoHeight={CONTEXT_PROPS.isAutoHeight}>
          <StyledToast
            autoDismiss={false}
            hide={!showErrorToast}
            onClose={() => setShowErrorToast(false)}
          >
            <TagsCopy id={Tags.TagDoesNotMeetGuidelines} />{' '}
            <TagsCopy id={Tags.TagGuidelinesSupportMessage} />
          </StyledToast>
          <SlideoverStackTransition>
            {viewStack
              .map(toViewComponent)
              // eslint-disable-next-line react/display-name
              .map((View, idx) => (isEntered: boolean) => (
                <View key={idx} isEntered={isEntered} />
              ))}
          </SlideoverStackTransition>
        </Container>
      </TagsContext.Provider>
    );
  });
ConnectedTagsModalView.displayName = 'ConnectedTagsModalView';

export const TagsModalView = withRouter(ConnectedTagsModalView);

export const setLocationHistory = (
  routerHistory: NextRouter,
  viewStack: TagModalView[],
  queryProps: QueryProps,
  sourceProps: SourceProps,
) => {
  const { stringifiedTagsQueryParams, tagsQueryParams } = toTagsModalQuery(
    queryProps.view,
    queryProps?.tagName,
    queryProps?.browseCategory,
  );

  routerHistory.push(
    {
      query: {
        ...tagsQueryParams,
        viewStack,
        source: toAnalyticsTagView(sourceProps.tagView, sourceProps?.category),
      },
    },
    stringifiedTagsQueryParams,
    { shallow: true },
  );
};

export const onSetView = (
  queryProps: QueryProps,
  lastActiveBrowseCategory: TagBrowseCategorySlug,
  routerHistory: NextRouter,
  viewStack: TagModalView[],
  viewSetters: ViewSetters,
  currentView?: TagModalView,
) => {
  if (isDefined(queryProps.browseCategory)) {
    viewSetters.setLastActiveBrowseCategory(queryProps.browseCategory);
  }
  if (isDefined(queryProps.tagName)) {
    viewSetters.setLastActiveTagName(queryProps.tagName);
  }
  const sourceProps = {
    tagView: currentView!,
    category: lastActiveBrowseCategory,
  };
  if (isPersistentNavBarView(currentView!) && isPersistentNavBarView(queryProps.view)) {
    viewSetters.setViewStack([...viewStack.slice(0, -1), queryProps.view]); // If navigating within main view, replace current view
  } else {
    viewSetters.setViewStack([...viewStack, queryProps.view]); // Add new view
  }
  if (isAutoHeightView(queryProps.view)) {
    viewSetters.setIsAutoHeight(true);
  }
  setLocationHistory(routerHistory, viewStack, queryProps, sourceProps);
};

export const onGoBack = (
  lastActiveBrowseCategory: TagBrowseCategorySlug,
  routerHistory: NextRouter,
  goBackStack: TagModalView[],
  setViewStack: (stack: any) => void,
  setIsAutoHeight: (isAutoHeight: boolean) => void,
  lastActiveTagName?: string,
  goBackTo?: TagModalView,
  currentView?: TagModalView,
) => {
  const queryProps = {
    view: goBackTo!,
    tagName: goBackTo === TagModalView.TagDetails ? lastActiveTagName : undefined,
    browseCategory:
      goBackTo === TagModalView.BrowseTags ? lastActiveBrowseCategory : undefined,
  };
  const sourceProps = {
    tagView: currentView!,
    category: lastActiveBrowseCategory,
  };

  setViewStack([...goBackStack]);
  setLocationHistory(routerHistory, goBackStack, queryProps, sourceProps);
  setIsAutoHeight(false);
};

export const toGoBackStack = (
  viewStack: TagModalView[],
  currentView: TagModalView = TagModalView.MyTags,
) => {
  const goBackStack = viewStack.slice(0, -1);
  while (
    goBackStack.slice(-1)?.[0] === TagModalView.ReplaceTag ||
    goBackStack.slice(-1)?.[0] === currentView
  ) {
    goBackStack.pop(); // Go back an extra view if the previous view was ReplaceTag or the same view
  }
  return goBackStack;
};

export const useTagsModalProps = (
  routerHistory: NextRouter,
  setShowErrorToast: (showError: boolean) => void,
) => {
  const previousViews = routerHistory.query?.viewStack ?? [];
  const view = getTagViewFromLocation(routerHistory.query);
  const [viewStack, setViewStack] = useState([...previousViews, view]);
  const [lastScroll, setLastScroll] = useState(0);
  const goBackStack = viewStack.length > 0 ? toGoBackStack(viewStack, view) : [];
  const goBackTo = goBackStack.slice(-1)?.[0];
  const [lastActiveBrowseCategory, setLastActiveBrowseCategory] =
    React.useState<TagBrowseCategorySlug>(TagBrowseCategorySlug.Trending);
  const [lastActiveTagName, setLastActiveTagName] = React.useState('');
  const [persistentNavOffset, setPersistentNavOffset] = React.useState(0);
  const [isAutoHeight, setIsAutoHeight] = React.useState(false);

  const CONTEXT_PROPS = {
    setView: (
      newView: TagModalView,
      tagName?: string,
      browseCategory?: TagBrowseCategorySlug,
    ) => {
      const queryProps = {
        view: newView,
        tagName,
        browseCategory,
      };
      const viewSetters = {
        setLastActiveBrowseCategory,
        setLastActiveTagName,
        setViewStack,
        setIsAutoHeight,
      };

      return onSetView(
        queryProps,
        lastActiveBrowseCategory,
        routerHistory,
        viewStack,
        viewSetters,
        view,
      );
    },
    goBack: () =>
      onGoBack(
        lastActiveBrowseCategory,
        routerHistory,
        goBackStack,
        setViewStack,
        setIsAutoHeight,
        lastActiveTagName,
        goBackTo,
        view,
      ),
    goBackTo,
    setShowErrorToast,
    showBackButton: goBackStack.length > 0,
    lastScroll,
    setLastScroll,
    lastActiveBrowseCategory,
    setLastActiveBrowseCategory,
    persistentNavOffset,
    setPersistentNavOffset,
    setLastActiveTagName,
    isAutoHeight,
    setIsAutoHeight,
  };

  return {
    CONTEXT_PROPS,
    viewStack,
    goBackStack,
    setViewStack,
    goBackTo,
    view,
  };
};

const toViewComponent = (
  view: TagModalView,
): React.ComponentType<React.PropsWithChildren<{ isEntered: boolean }>> => {
  switch (view) {
    case TagModalView.BrowseTags:
      return MainView;
    case TagModalView.SearchAndCreate:
      return MainView;
    case TagModalView.TagDetails:
      return TagDetails;
    case TagModalView.ReplaceTag:
      return ReplaceTagView;
    case TagModalView.OtherMembersTags:
      return OtherMembersTagsView;
    case TagModalView.ConfirmHideTag:
      return ConfirmHideTagView;
    case TagModalView.MyTags:
    default:
      return MainView;
  }
};

const isPersistentNavBarView = (view: TagModalView) =>
  PERSISTENT_NAV_BAR_VIEWS.includes(view);

const isAutoHeightView = (view?: TagModalView) =>
  view && view === TagModalView.ConfirmHideTag;

export const Container = styled(StandardModalContainer)<{ isAutoHeight?: boolean }>`
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${gray1};
  height: 100vh;
  overflow-x: auto;
  ${media.tablet<{ isAutoHeight?: boolean }>`
    padding: 0;
    height: ${p => (p.isAutoHeight ? 'auto' : modalHeight)};
    max-height: ${tagsModalMaxHeight};
    min-height: ${tagsModalMinHeight};
  `}

  /* hide scrollbar*/
  ::-webkit-scrollbar {
    height: 0;
    width: 0;
    background: transparent;
  }
  ${ieHideScroll}
`;

const TOAST_WIDTH = 460;

const StyledToast = styled(CustomizableToast)`
  width: 100%;
  top: 0;
  position: absolute;
  z-index: 2;

  ${media.tablet`
    width: ${TOAST_WIDTH}px;
    top: ${spaces.large}px;
    left: calc(50% - ${TOAST_WIDTH / 2}px);
  `}

  ${StyledMessageAndIcon} {
    padding: ${spaces.small}px 0;

    span {
      min-height: ${spaces.xLarge}px;
    }
  }
`;
StyledToast.displayName = 'StyledToast';
